import React, { useEffect, useCallback, useState } from 'react';
import { Button, Input, DatePicker, Radio, Table, Tabs, message, Modal, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useB255Mode } from './hooks/useB255Mode';
import { useB255Record } from './hooks/useB255Record';
import { useB255Navigation } from './hooks/useB255Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import LookupField from '../../components/LookupField';
import DetailTable from './components/DetailTable';
import './B255Form.css';

const { TextArea } = Input;
const { TabPane } = Tabs;

const B255Form: React.FC = () => {
  const { mode, setMode, isEditing, enterAdd, enterEdit, enterBrowse } = useB255Mode();
  const permissions = useModulePermission('pwb255f1');

  const {
    record, formData, details, messages, loading,
    loadRecord, getField, setField,
    resetForAdd, revertToRecord,
    addDetailRow, deleteDetailRow, updateDetailField,
    handleSave, handleDelete,
  } = useB255Record(setMode);

  const [quickSearch, setQuickSearch] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  const loadRecordWithNav = useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]);
  const { allIds, currentIdx, setCurrentIdx, loadList, navFirst, navPrev, navNext, navLast } =
    useB255Navigation(loadRecordWithNav);

  useEffect(() => {
    loadList().then((ids) => {
      if (ids.length > 0) { setCurrentIdx(0); loadRecord(ids[0]); }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNew = () => { resetForAdd(); enterAdd(); setActiveTab('1'); };
  const handleEditStart = () => {
    if (!record) return;
    if (record.fb650_80 === 'P') { message.warning('已核准的單據不可修改'); return; }
    enterEdit();
  };
  const handleRevert = () => { revertToRecord(); enterBrowse(); };
  const handleSaveAndReturn = async () => { await handleSave(); };
  const handleDeleteConfirm = () => {
    if (!record) return;
    if (record.fb650_80 === 'P') { message.warning('已核准的單據不可刪除'); return; }
    Modal.confirm({
      title: '確認刪除', content: '請確認刪除本資料!',
      onOk: async () => {
        await handleDelete();
        loadList().then((ids) => { if (ids.length > 0) { setCurrentIdx(0); loadRecord(ids[0]); } });
      },
    });
  };
  const handleQuickSearch = () => { if (quickSearch.trim()) loadRecord(quickSearch.trim()); };

  const isApproved = record?.fb650_80 === 'P';

  // Tab 1 — Basic Info
  const renderTab1 = () => (
    <div className="form-grid">
      <label>領用單號</label>
      <Input value={formData.fb650_01 || '（系統產生）'} disabled size="small" />
      <label>核准狀態</label>
      <span className={`status-badge ${isApproved ? 'status-approved' : 'status-pending'}`}>
        {formData.fb650_80 === 'P' ? '已核准' : formData.fb650_80 === 'B' ? '已沖銷' : '未核准'}
      </span>
      <label>領用日期 *</label>
      <DatePicker value={formData.fb650_11 ? dayjs(formData.fb650_11) : null} disabled={!isEditing} size="small"
        onChange={d => setField('fb650_11', d ? d.toISOString() : null)} />
      <label>文具用途 *</label>
      <Radio.Group value={formData.fb650_42} disabled={!isEditing} onChange={e => setField('fb650_42', e.target.value)}>
        <Radio value={1}>一般文具</Radio>
        <Radio value={3}>其他特殊用途</Radio>
      </Radio.Group>
      <label>部門代號 *</label>
      <LookupField lookupType="department" value={formData.fb650_10 || ''}
        onChange={v => setField('fb650_10', v)} onCodeConfirm={v => setField('fb650_10', v)}
        disabled={!isEditing} maxLength={8} style={{ width: '100%' }} />
      <label>人員代號</label>
      <LookupField lookupType="employee" value={formData.fb650_12 || ''}
        onChange={v => setField('fb650_12', v)} onCodeConfirm={v => setField('fb650_12', v)}
        disabled={!isEditing} maxLength={8} style={{ width: '100%' }} />
      <label>說明</label>
      <Input value={formData.fb650_15} disabled={!isEditing} size="small" maxLength={80} className="full-width"
        onChange={e => setField('fb650_15', e.target.value)} />
      <label>備註</label>
      <TextArea value={formData.fb650_30} disabled={!isEditing} rows={3} className="full-width"
        onChange={e => setField('fb650_30', e.target.value)} />
    </div>
  );

  // Tab 3 — Messages
  const renderTab3 = () => (
    <>
    <div style={{ marginBottom: 8, fontWeight: 500 }}>
      <span>留言項數: {formData.fb650_41}</span>
    </div>
    <Table dataSource={messages} rowKey={(_, idx) => String(idx)} size="small" pagination={false} columns={[
      { title: '序號', key: 'seq', render: (_, __, idx) => idx + 1, width: 60 },
      { title: '發送日期', dataIndex: 'fb652_12', width: 110, render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
      { title: '發送人代號', dataIndex: 'fb652_11', width: 100 },
      { title: '發送人姓名', dataIndex: 'fb652_11a', width: 100 },
      { title: '受言人', dataIndex: 'fb652_13', width: 100 },
      { title: '留言內容', dataIndex: 'fb652_03' },
    ]} />
    </>
  );

  // Action Bar
  const renderActionBar = () => (
    <div className="action-bar">
      <Space className="nav-bar">
        <Button size="small" onClick={navFirst} disabled={isEditing || currentIdx <= 0}>◀◀</Button>
        <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>◀</Button>
        <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= allIds.length - 1}>▶</Button>
        <Button size="small" onClick={navLast} disabled={isEditing || currentIdx >= allIds.length - 1}>▶▶</Button>
        <span style={{ fontSize: 12 }}>{allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '0 / 0'}</span>
      </Space>
      {!isEditing ? (
        <>
          <Button size="small" type="primary" onClick={handleAddNew} disabled={!permissions?.canCreate}>新增</Button>
          <Button size="small" onClick={handleEditStart} disabled={!permissions?.canEdit || isApproved || !record}>修改</Button>
          <Button size="small" danger onClick={handleDeleteConfirm} disabled={!permissions?.canDelete || isApproved || !record}>刪除</Button>
        </>
      ) : (
        <>
          <Button size="small" type="primary" onClick={handleSaveAndReturn} loading={loading}>存檔</Button>
          <Button size="small" onClick={handleRevert} disabled={loading}>還原</Button>
        </>
      )}
    </div>
  );

  return (
    <div className="b255-form">
      <div className="quick-search">
        <Input placeholder="快速搜尋領用單號" value={quickSearch} size="small" style={{ width: 200 }}
          onChange={e => setQuickSearch(e.target.value)} onPressEnter={handleQuickSearch} disabled={isEditing} />
        <Button size="small" onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
      </div>
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本資料" key="1">{renderTab1()}</TabPane>
          <TabPane tab="明細資料" key="2">
            <DetailTable
              rows={details} isEditing={isEditing}
              onUpdate={updateDetailField} onDelete={deleteDetailRow} onAdd={addDetailRow}
              totalCount={formData.fb650_16} totalAmount={formData.fb650_17}
            />
          </TabPane>
          <TabPane tab="留言訊息" key="3">{renderTab3()}</TabPane>
        </Tabs>
      </Spin>
      {renderActionBar()}
    </div>
  );
};

export default B255Form;
